import type { FieldShape } from './field';
import { fieldPipeRenderSegments } from './renderer';
import type { FateMapDefinition } from './types';

const n = (value: number) => Number(value.toFixed(2));

function shapeMarkup(shape: FieldShape, color: string): string {
  switch (shape.kind) {
    case 'room': return `<rect x="${n(shape.x)}" y="${n(shape.y)}" width="${n(shape.w)}" height="${n(shape.h)}" rx="${n(shape.r ?? 18)}" fill="${color}"/>`;
    case 'disc': return `<circle cx="${n(shape.x)}" cy="${n(shape.y)}" r="${n(shape.r)}" fill="${color}"/>`;
    case 'ellipse': return `<ellipse cx="${n(shape.x)}" cy="${n(shape.y)}" rx="${n(shape.rx)}" ry="${n(shape.ry)}" fill="${color}"/>`;
    case 'poly': return `<polygon points="${shape.points.map(({ x, y }) => `${n(x)},${n(y)}`).join(' ')}" fill="${color}"/>`;
    case 'pipe': return fieldPipeRenderSegments(shape).map((part) => `<path d="M${n(part.x0)} ${n(part.y0)}L${n(part.x1)} ${n(part.y1)}" fill="none" stroke="${color}" stroke-width="${n(part.width)}" stroke-linecap="round"/>`).join('');
  }
}

/** 선택 카드의 미니 도면. 별도 그림을 유지하지 않고 실제 벽·구멍 스펙을 축소한다. */
export function mapPreviewMarkup(map: FateMapDefinition): string {
  const field = map.field;
  if (!field) return '';
  const maskId = `fate-preview-${map.id}`;
  const markerSize = Math.max(map.worldWidth, map.worldHeight) * .017;
  const start = field.spawn.kind === 'rect'
    ? { x: field.spawn.x + field.spawn.w / 2, y: field.spawn.y + field.spawn.h / 2 }
    : {
      x: field.spawn.cx + Math.cos((field.spawn.from + field.spawn.to) / 2) * (field.spawn.r - (field.spawn.rows - 1) * field.spawn.pitch / 2),
      y: field.spawn.cy + Math.sin((field.spawn.from + field.spawn.to) / 2) * (field.spawn.r - (field.spawn.rows - 1) * field.spawn.pitch / 2),
    };
  const finish = field.finish.kind === 'rect'
    ? { x: field.finish.x + field.finish.w / 2, y: field.finish.y + field.finish.h / 2 }
    : { x: field.finish.x, y: field.finish.y };
  const fixtures = field.obstacles.flatMap((obstacle) => {
    if (obstacle.kind !== 'bumper' && obstacle.kind !== 'pin') return [];
    return `<circle cx="${n(obstacle.x)}" cy="${n(obstacle.y)}" r="${n(obstacle.r)}" fill="${map.accent}"/>`;
  }).join('');
  return `<svg viewBox="0 0 ${map.worldWidth} ${map.worldHeight}" preserveAspectRatio="xMidYMid meet" aria-hidden="true" focusable="false">
    <defs><mask id="${maskId}" maskUnits="userSpaceOnUse" x="0" y="0" width="${map.worldWidth}" height="${map.worldHeight}">
      ${field.shapes.map((shape) => shapeMarkup(shape, '#fff')).join('')}
      ${field.holes.map((shape) => shapeMarkup(shape, '#000')).join('')}
    </mask></defs>
    <g mask="url(#${maskId})"><rect width="${map.worldWidth}" height="${map.worldHeight}" fill="#6874b8"/>${fixtures}</g>
    <circle data-marker="start" cx="${n(start.x)}" cy="${n(start.y)}" r="${n(markerSize)}" fill="#3ef2b6"/>
    <rect data-marker="finish" x="${n(finish.x - markerSize)}" y="${n(finish.y - markerSize)}" width="${n(markerSize * 2)}" height="${n(markerSize * 2)}" fill="#fff"/>
  </svg>`;
}
